import React, { useState, useEffect } from 'react';
import { WalletBalance } from '../../components/wallet/WalletBalance';
import { TransactionHistory } from '../../components/wallet/TransactionHistory';
// Temporarily hidden from the wallet page (restore when re-enabled):
// import { CurrencyConverter } from '../../components/wallet/CurrencyConverter';
import { WalletDeposit } from '../../components/wallet/WalletDeposit';
import { WalletTransaction, walletService } from '../../services/wallet.service';
import toast from 'react-hot-toast';

export default function WalletPage() {
  const [balance, setBalance] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [selectedCurrency, setSelectedCurrency] = useState<string>('MYR');

  const loadBalance = async (currency?: string) => {
    setLoading(true);
    try {
      const data = await walletService.getBalance(currency);
      setBalance(data);
    } catch (error: any) {
      console.error('Failed to load wallet balance:', error);
      toast.error(error.response?.data?.message || 'Failed to load wallet balance');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBalance();
  }, []);

  const handleCurrencyChange = async (currency: string) => {
    setSelectedCurrency(currency);
    await loadBalance(currency);
    try {
      await walletService.updateWalletCurrency(currency);
      toast.success(`Wallet currency updated to ${currency}`);
    } catch (error: any) {
      console.error('Failed to update currency:', error);
      toast.error(error.response?.data?.message || 'Failed to update currency preference');
    }
  };

  const handleTransactionClick = (transaction: WalletTransaction) => {
    console.log('Transaction clicked:', transaction);
    // You could open a modal with transaction details here
    toast.success(`Transaction: ${transaction.description}`);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const isNewWallet = balance?.isNewWallet ?? false;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            My Wallet
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage your wallet balance, transactions, and currency preferences
          </p>
        </div>

        {/* Main Content */}
        <div className="space-y-6">
          {/* Welcome card for new wallets — full width, above balance */}
          {isNewWallet && (
            <div>
              <WalletDeposit
                isNewWallet
                pendingDeposit={balance?.pendingDeposit}
                currency={balance?.currency}
                activationFee={balance?.activationFee}
                hasReferralDiscount={balance?.hasReferralDiscount}
              />
            </div>
          )}

          {/* Wallet Balance (Full Width) */}
          <div>
            {balance && (
              <WalletBalance
                balance={balance}
                onCurrencyChange={handleCurrencyChange}
              />
            )}
          </div>

          {/* Deposit + Converter — existing wallets only. Converter is
              temporarily hidden; deposit takes its column until it returns. */}
          {!isNewWallet && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-3">
                <WalletDeposit
                  isNewWallet={false}
                  pendingDeposit={balance?.pendingDeposit}
                  currency={balance?.currency}
                />
              </div>
              {/* Temporarily hidden:
              <div className="lg:col-span-1">
                <CurrencyConverter />
              </div>
              */}
            </div>
          )}

          {/* Transaction History — existing wallets only */}
          {!isNewWallet && (
            <div>
              <TransactionHistory onTransactionClick={handleTransactionClick} />
            </div>
          )}
        </div>

        {/* Info Cards — hidden for new wallets */}
        {!isNewWallet && (
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Minimum Balance
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Maintain a minimum balance of RM 800 (MYR), S$240 (SGD), or $500 (USD) to keep your wallet active.
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Supported Currencies
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Store and manage your balance in MYR, SGD, USD, IDR, or THB. Easily convert between currencies.
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Transaction History
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              View all your wallet transactions with complete details and balance snapshots.
            </p>
          </div>
        </div>
        )}
      </div>
    </div>
  );
}
